import { Text } from '@lune-fe/lune-ui-lib'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { isAxiosError } from 'axios'
import type { OptionsObject, ProviderContext, SnackbarKey, SnackbarMessage } from 'notistack'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Err } from 'ts-results-es'

type ErrorSnackbarOptions = OptionsObject & {
    /**
     * Carries the original error value that caused this snackbar, we will attempt to
     * extract some identifying information from it.
     *
     * The type is kept intentionally under-defined so that the callers can pass errors
     * here without having to verify their types (useful in catch blocks).
     *
     * We try to handle all typical error types correctly (Axios errors, Lune TS client
     * errors, wrapper or not wrapped in ts-results-es Err, and errors stored in
     * `Error.cause`).
     */
    error?: unknown
}

export type ErrorEnqueueSnackbar = (
    message: SnackbarMessage,
    options?: ErrorSnackbarOptions,
) => SnackbarKey

type ErrorSnackbarContext = Omit<ProviderContext, 'enqueueSnackbar'> & {
    enqueueSnackbar: ErrorEnqueueSnackbar
}

function isObjectLike(value: unknown): value is object {
    return (typeof value === 'object' && value !== null) || typeof value === 'function'
}

function getProperty(value: unknown, propertyKey: PropertyKey): unknown {
    if (!isObjectLike(value)) {
        return undefined
    }

    return Reflect.get(value, propertyKey)
}

function getStringProperty(value: unknown, propertyKey: PropertyKey): string | undefined {
    const property = getProperty(value, propertyKey)
    return typeof property === 'string' && property.length > 0 ? property : undefined
}

function getTsResultsErrValue(value: unknown): unknown {
    return value instanceof Err ? value.error : undefined
}

function getDirectApiErrorRequestId(error: unknown): string | undefined {
    // Lune TS client ApiError values expose the request ID generated by the Lune
    // API server. It uniquely identifies the request and response pair.
    const requestId = getStringProperty(error, 'requestId')

    if (requestId) {
        return requestId
    }

    if (isAxiosError(error)) {
        // Lune production applications are currently behind Cloudflare and the
        // applications treat Cloudflare's cf-ray header as a de-facto request ID.
        const value = error.response?.headers['cf-ray']
        return value ? value.toString() : undefined
    }

    return undefined
}

/**
 * Returns a request ID that can be provided to Lune support to help identify the
 * request that produced an error, if one can be extracted.
 */
export function getApiErrorRequestId(error: unknown): string | undefined {
    // Callers sometimes pass errors wrapped in ts-results-es Err, while Lune TS
    // client errors are passed directly.
    const unwrappedError = getTsResultsErrValue(error) ?? error

    // Err.unwrap() throws an Error with the original error stored in `cause`.
    return (
        getDirectApiErrorRequestId(unwrappedError) ??
        getDirectApiErrorRequestId(getProperty(unwrappedError, 'cause'))
    )
}

/**
 * Returns the notistack snackbar context with an error-aware `enqueueSnackbar`.
 *
 * The returned function keeps the usual notistack
 * `enqueueSnackbar(message, options)` call shape. It differs from notistack by
 * accepting `options.error`, which is not forwarded to notistack and is only
 * used to append a support reference when the error contains a Lune request ID.
 *
 * Use this hook instead of notistack's `useSnackbar` when showing errors from
 * Lune API calls:
 *
 * ```
 * const { enqueueSnackbar } = useErrorSnackbar()
 * enqueueSnackbar(SnackbarMessages.GENERIC_ERROR, { error, variant: 'error' })
 * ```
 */
export function useErrorSnackbar(): ErrorSnackbarContext {
    const { enqueueSnackbar: notistackEnqueueSnackbar, closeSnackbar } = useSnackbar()

    const enqueueSnackbar = useCallback<ErrorEnqueueSnackbar>(
        (message, options): SnackbarKey => {
            if (options === undefined) {
                return notistackEnqueueSnackbar(message)
            }

            const { error, ...snackbarOptions } = options
            const resolvedRequestId = getApiErrorRequestId(error)

            if (!resolvedRequestId) {
                return notistackEnqueueSnackbar(message, snackbarOptions)
            }

            return notistackEnqueueSnackbar(
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        minWidth: 0,
                        color: 'inherit',
                    }}
                >
                    <Text component={'span'} variant={'body3'} sx={{ color: 'inherit' }}>
                        {message}
                    </Text>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            minWidth: 0,
                        }}
                    >
                        <Text
                            component={'span'}
                            variant={'body3'}
                            sx={{
                                color: 'inherit',
                                overflowWrap: 'anywhere',
                                minWidth: 0,
                                textAlign: 'left',
                            }}
                        >
                            {`Reference ID: ${resolvedRequestId}`}
                        </Text>
                        <CopyToClipboard
                            text={resolvedRequestId}
                            onCopy={() =>
                                notistackEnqueueSnackbar('Selection copied to the clipboard.')
                            }
                        >
                            <IconButton
                                type={'button'}
                                title={'Copy reference ID'}
                                size={'small'}
                                sx={{
                                    flexShrink: 0,
                                    color: 'inherit',
                                    p: 0.25,
                                }}
                            >
                                <ContentCopyIcon fontSize={'small'} />
                            </IconButton>
                        </CopyToClipboard>
                    </Box>
                </Box>,
                snackbarOptions,
            )
        },
        [notistackEnqueueSnackbar],
    )

    return { enqueueSnackbar, closeSnackbar }
}
